import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Box, IconPerson, Text } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'

type Props = {
  point: any
  id: number
  isActive: boolean
  setCurrentPoint: any
  setIsActive: (id: number) => void
  parentRef?: any
  ref?: any
}

const Wrapper = styled.div<{ isActive: boolean }>`
  transition: background-color 0.3s ease-in;
  cursor: pointer;
  border-bottom: 1px solid ${vars.borderColor.neutralLight};
  background-color: ${props =>
    props.isActive ? `${vars.backgroundColor.disabled}` : `none`};
`

const PoiListItem = (
  { point, id, isActive, setCurrentPoint, setIsActive, parentRef }: Props,
  ref: any
) => {
  const topPos = parentRef?.current?.offsetTop
  const [firstRender, setFirstRender] = useState(true)
  const { t } = useTranslation('poi')
  const handleSelect = () => {
    setCurrentPoint({
      name: point?.info?.name,
      address: point?.info?.address,
      distance_walktime: point.distance_walktime,
      distance: point.distance,
      latitude: point.latitude,
      longitude: point.longitude
    })
    setIsActive(id)
    parentRef?.current?.scroll({
      behavior: 'smooth',
      top: ref?.current?.offsetTop - topPos
    })
  }

  useEffect(() => {
    if (isActive && !firstRender) {
      handleSelect()
    }

    setFirstRender(false)
  }, [isActive, firstRender])

  const walkTime = point?.distance_walktime
  const hasAddress = (point?.info?.address || '').trim().length !== 0

  return (
    <Wrapper isActive={isActive} onClick={handleSelect} ref={ref}>
      <Box
        display="flex"
        flexDirection="column"
        gap="xsmall"
        width="full"
        paddingY="gutter"
        paddingX="small"
      >
        <Box
          display="flex"
          alignItems="flexStart"
          justifyContent="spaceBetween"
          width="full"
          gap="small"
        >
          <Text size="standard" weight="medium">
            {point?.info?.name}
          </Text>
          <Box display="flex" alignItems="center" gap="xxxsmall" flexShrink={0}>
            <Text size="small">
              <IconPerson />
            </Text>
            <Text size="small">
              <Trans
                i18nKey="poi:b.listings.poi.distance_walktime_min"
                values={{ mins: walkTime }}
                defaults="{{ mins }} min walk"
              />
            </Text>
            <Text size="small" tone="tertiary">
              ({(point?.distance / 1000).toFixed(1)}{' '}
              {t('poi:b.listings.poi.distance_walktime_km', 'km')})
            </Text>
          </Box>
        </Box>
        {hasAddress ? (
          <Box
            display="flex"
            alignItems="flexStart"
            justifyContent="spaceBetween"
            width="full"
          >
            <Text size="small" tone="tertiary">
              {point.info.address}
            </Text>
          </Box>
        ) : (
          (point?.info?.type === 'cafes' ||
            point?.info?.type === 'restaurants' ||
            point?.info?.type === 'grocery_stores' ||
            point?.info?.type === 'shopping_centers') && (
            <Box
              display="flex"
              alignItems="flexStart"
              justifyContent="spaceBetween"
              width="full"
            >
              <Text size="small" tone="tertiary">
                {t(
                  'poi:b.listings.poi.address_not_available',
                  'Address not available'
                )}
              </Text>
            </Box>
          )
        )}
      </Box>
    </Wrapper>
  )
}

export default React.forwardRef(PoiListItem)
